// Monero adapter — keys, address and the 25-word export. Derivation only; the
// chain is NOT registered in SUPPORTED_CHAINS, because reading a balance needs
// a view-key scanner we do not run yet and a chain that appears in the wallet
// with no balance and no deposits is worse than one that does not appear.
//
// Three places where Monero looks like something it is not:
//
// 1. Derivation is NOT BIP-32/44 in the usual sense. We take 32 bytes from a
//    BIP-32 path for determinism, but the key is then reduced mod the ed25519
//    group order and used directly. Monero itself has no BIP-39 standard, so
//    this scheme is ours; what makes it portable is the 25-word export below,
//    which every Monero wallet reads.
// 2. The public key is NOT standard ed25519. `getPublicKey` hashes and clamps
//    the private key before multiplying, per RFC 8032. Monero multiplies the
//    reduced scalar directly. Using the standard function would produce
//    well-formed addresses whose keys nobody holds.
// 3. Base58 is NOT standard base58. Monero encodes in 8-byte blocks of 11
//    characters each, so a general-purpose decoder gets a different string.

import { HDKey } from '@scure/bip32';
import type { DerivedAddresses } from '../types';
import { keccak_256 } from '@noble/hashes/sha3';
import { ed25519 } from '@noble/curves/ed25519';
import { MONERO_PREFIX_LENGTH, MONERO_WORDS } from './wordlist';

/** SLIP-44 coin type 128. All hardened above the change level, like our others. */
const PATH = (index: number) => `m/44'/128'/0'/0/${index}`;

/** Standard mainnet address prefix. 0x13 is integrated, 0x2a is subaddress. */
const MAINNET_PREFIX = 0x12;

/** The ed25519 group order, l = 2^252 + 27742317777372353535851937790883648493. */
const L = ed25519.CURVE.n;

const WORD_COUNT = BigInt(MONERO_WORDS.length);

export interface MoneroKeys {
  /** Private spend key — what the 25 words encode. */
  secretSpendKey: Uint8Array;
  /** Private view key — derived from the spend key, never independent. */
  secretViewKey: Uint8Array;
  publicSpendKey: Uint8Array;
  publicViewKey: Uint8Array;
  /** Standard mainnet address, 95 characters. */
  address: string;
}

function bytesToLeBigInt(b: Uint8Array): bigint {
  let n = 0n;
  for (let i = b.length - 1; i >= 0; i--) n = (n << 8n) | BigInt(b[i]!);
  return n;
}

function leBigIntToBytes(n: bigint, length = 32): Uint8Array {
  const out = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    out[i] = Number(n & 0xffn);
    n >>= 8n;
  }
  return out;
}

/** `sc_reduce32`: interpret the bytes little-endian and reduce mod l. */
export function scReduce32(b: Uint8Array): Uint8Array {
  if (b.length !== 32) throw new Error('scReduce32 expects 32 bytes');
  return leBigIntToBytes(bytesToLeBigInt(b) % L);
}

/** Public key for a Monero scalar: sG with the scalar used as-is. */
function publicKeyFor(secret: Uint8Array): Uint8Array {
  const scalar = bytesToLeBigInt(secret);
  if (scalar === 0n) throw new Error('monero: zero scalar');
  return ed25519.ExtendedPoint.BASE.multiply(scalar).toRawBytes();
}

/** The view key is a deterministic function of the spend key, which is why one
 *  25-word phrase restores a whole wallet. */
export function moneroViewKeyFrom(secretSpendKey: Uint8Array): Uint8Array {
  return scReduce32(keccak_256(secretSpendKey));
}

// ── Monero base58 ───────────────────────────────────────────────────────────
// Full 8-byte blocks encode to exactly 11 characters; the trailing partial
// block uses the size below. Encoding each block big-endian and padding to a
// FIXED width is the whole difference from standard base58, which would drop
// leading zeros and shift everything after them.

const B58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const FULL_BLOCK_BYTES = 8;
const FULL_BLOCK_CHARS = 11;
/** Characters a partial block of n bytes encodes to, n = 0..7. */
const BLOCK_CHARS = [0, 2, 3, 5, 6, 7, 9, 10, 11];

function encodeBlock(block: Uint8Array): string {
  let num = 0n;
  for (const byte of block) num = (num << 8n) | BigInt(byte);
  const width = BLOCK_CHARS[block.length]!;
  let out = '';
  for (let i = 0; i < width; i++) {
    out = B58_ALPHABET[Number(num % 58n)] + out;
    num /= 58n;
  }
  return out;
}

function decodeBlock(chars: string, byteLength: number): Uint8Array {
  let num = 0n;
  for (const ch of chars) {
    const digit = B58_ALPHABET.indexOf(ch);
    if (digit < 0) throw new Error('monero base58: bad character');
    num = num * 58n + BigInt(digit);
  }
  const out = new Uint8Array(byteLength);
  for (let i = byteLength - 1; i >= 0; i--) {
    out[i] = Number(num & 0xffn);
    num >>= 8n;
  }
  if (num !== 0n) throw new Error('monero base58: block overflow');
  return out;
}

export function moneroBase58Encode(data: Uint8Array): string {
  let out = '';
  let offset = 0;
  while (data.length - offset >= FULL_BLOCK_BYTES) {
    out += encodeBlock(data.subarray(offset, offset + FULL_BLOCK_BYTES));
    offset += FULL_BLOCK_BYTES;
  }
  if (offset < data.length) out += encodeBlock(data.subarray(offset));
  return out;
}

export function moneroBase58Decode(text: string): Uint8Array {
  const chunks: Uint8Array[] = [];
  let offset = 0;
  while (text.length - offset >= FULL_BLOCK_CHARS) {
    chunks.push(decodeBlock(text.slice(offset, offset + FULL_BLOCK_CHARS), FULL_BLOCK_BYTES));
    offset += FULL_BLOCK_CHARS;
  }
  const tail = text.length - offset;
  if (tail > 0) {
    const byteLength = BLOCK_CHARS.indexOf(tail);
    if (byteLength < 0) throw new Error('monero base58: bad length');
    chunks.push(decodeBlock(text.slice(offset), byteLength));
  }
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

// ── Address ─────────────────────────────────────────────────────────────────

/** prefix ‖ spend ‖ view ‖ first 4 bytes of keccak over those, in base58. */
export function moneroAddress(
  publicSpendKey: Uint8Array,
  publicViewKey: Uint8Array,
  prefix = MAINNET_PREFIX,
): string {
  const body = new Uint8Array(1 + 32 + 32);
  body[0] = prefix;
  body.set(publicSpendKey, 1);
  body.set(publicViewKey, 33);
  const checksum = keccak_256(body).subarray(0, 4);
  const full = new Uint8Array(body.length + 4);
  full.set(body);
  full.set(checksum, body.length);
  return moneroBase58Encode(full);
}

/** Whether a string is a well-formed standard mainnet address. */
export function isValidMoneroAddress(address: string): boolean {
  // 95 characters for a standard address; integrated (106) and subaddresses
  // are deliberately not accepted — we never produce them, and accepting an
  // integrated address would silently drop its payment id.
  if (address.length !== 95) return false;
  let decoded: Uint8Array;
  try {
    decoded = moneroBase58Decode(address);
  } catch {
    return false;
  }
  if (decoded.length !== 69) return false;
  if (decoded[0] !== MAINNET_PREFIX) return false;
  const body = decoded.subarray(0, 65);
  const expected = keccak_256(body).subarray(0, 4);
  const actual = decoded.subarray(65);
  for (let i = 0; i < 4; i++) if (expected[i] !== actual[i]) return false;
  return true;
}

// ── 25-word phrase ──────────────────────────────────────────────────────────

/** CRC-32, for the checksum word. Table built once, on first use. */
const crcTable = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(text: string): number {
  let crc = 0xffffffff;
  for (let i = 0; i < text.length; i++) {
    crc = crcTable[(crc ^ text.charCodeAt(i)) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Which of the 24 words is repeated as the 25th. */
function checksumIndex(words: readonly string[]): number {
  const prefixes = words.map((w) => w.slice(0, MONERO_PREFIX_LENGTH)).join('');
  return crc32(prefixes) % words.length;
}

/**
 * The 25-word phrase for a 32-byte key — the format every Monero wallet reads.
 *
 * This is what makes a key derived by our own scheme portable: the phrase is
 * not a second wallet, it is the same key written the way Monero writes it. A
 * restoring wallet also wants a restore height, or it scans from genesis.
 */
export function moneroKeyToWords(key: Uint8Array): string {
  if (key.length !== 32) throw new Error('monero mnemonic expects 32 bytes');
  const words: string[] = [];
  for (let i = 0; i < key.length; i += 4) {
    // Four bytes, little-endian, become three words. Not eleven bits each as
    // in BIP-39 — the arithmetic below is the encoding, not a bit packing.
    const val =
      BigInt(key[i]!) |
      (BigInt(key[i + 1]!) << 8n) |
      (BigInt(key[i + 2]!) << 16n) |
      (BigInt(key[i + 3]!) << 24n);
    const w1 = val % WORD_COUNT;
    const w2 = (val / WORD_COUNT + w1) % WORD_COUNT;
    const w3 = (val / WORD_COUNT / WORD_COUNT + w2) % WORD_COUNT;
    words.push(MONERO_WORDS[Number(w1)]!, MONERO_WORDS[Number(w2)]!, MONERO_WORDS[Number(w3)]!);
  }
  words.push(words[checksumIndex(words)]!);
  return words.join(' ');
}

/**
 * The 32-byte key a phrase encodes. Accepts 24 words (no checksum) or 25, and
 * verifies the checksum when it is there — a mistyped word that happens to be
 * in the list would otherwise restore a different, empty wallet in silence.
 */
export function moneroWordsToKey(phrase: string): Uint8Array {
  const words = phrase.trim().toLowerCase().split(/\s+/);
  if (words.length !== 24 && words.length !== 25) {
    throw new Error('monero mnemonic must be 24 or 25 words');
  }
  const body = words.slice(0, 24);
  if (words.length === 25 && words[24] !== body[checksumIndex(body)]) {
    throw new Error('monero mnemonic checksum mismatch');
  }

  const indices = body.map((w) => {
    const at = MONERO_WORDS.indexOf(w);
    if (at < 0) throw new Error(`monero mnemonic: unknown word "${w}"`);
    return BigInt(at);
  });

  const out = new Uint8Array(32);
  for (let group = 0; group < 8; group++) {
    const [w1, w2, w3] = [indices[group * 3]!, indices[group * 3 + 1]!, indices[group * 3 + 2]!];
    // The inverse of the encoding above, and the parentheses are the whole of
    // it: `L * (x % L)` is not `L * x % L`. Written the other way this decoded
    // every group to roughly w1, and only the published vector showed it —
    // encoding still round-tripped against itself.
    const v =
      (w1 +
        WORD_COUNT * ((WORD_COUNT - w1 + w2) % WORD_COUNT) +
        WORD_COUNT * WORD_COUNT * ((WORD_COUNT - w2 + w3) % WORD_COUNT)) %
      4294967296n;
    out[group * 4] = Number(v & 0xffn);
    out[group * 4 + 1] = Number((v >> 8n) & 0xffn);
    out[group * 4 + 2] = Number((v >> 16n) & 0xffn);
    out[group * 4 + 3] = Number((v >> 24n) & 0xffn);
  }
  return out;
}

// ── Derivation ──────────────────────────────────────────────────────────────

/** Every key and the address for one account index, from the wallet seed. */
export function deriveMoneroKeys(seed: Uint8Array, index = 0): MoneroKeys {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.privateKey) throw new Error('Monero derivation failed');

  const secretSpendKey = scReduce32(child.privateKey);
  const secretViewKey = moneroViewKeyFrom(secretSpendKey);
  const publicSpendKey = publicKeyFor(secretSpendKey);
  const publicViewKey = publicKeyFor(secretViewKey);

  return {
    secretSpendKey,
    secretViewKey,
    publicSpendKey,
    publicViewKey,
    address: moneroAddress(publicSpendKey, publicViewKey),
  };
}

/**
 * The address and public key for an account index, in the shape every other
 * adapter returns — so the wallet can key derivation by chain without a
 * per-chain special case.
 *
 * Only these two values leave this function. The spend key and the view key
 * stay with `deriveMoneroKeys`, which the device calls when it needs to scan
 * or sign; registering a Monero wallet with the backend must never carry the
 * view key, because a view key is enough to watch every payment the user ever
 * receives.
 */
export function deriveMoneroAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const keys = deriveMoneroKeys(seed, index);
  return {
    eoa: keys.address,
    // The public SPEND key. Public, and useless on its own for watching: an
    // observer needs the private view key to see anything.
    publicKey: Buffer.from(keys.publicSpendKey).toString('hex'),
  };
}

/** The 25-word phrase for this wallet's Monero account — what the user takes
 *  to any other Monero wallet. */
export function moneroExportPhrase(seed: Uint8Array, index = 0): string {
  return moneroKeyToWords(deriveMoneroKeys(seed, index).secretSpendKey);
}

/** Keys and address for a phrase restored from elsewhere. */
export function moneroKeysFromWords(phrase: string): MoneroKeys {
  const secretSpendKey = scReduce32(moneroWordsToKey(phrase));
  const secretViewKey = moneroViewKeyFrom(secretSpendKey);
  const publicSpendKey = publicKeyFor(secretSpendKey);
  const publicViewKey = publicKeyFor(secretViewKey);
  return {
    secretSpendKey,
    secretViewKey,
    publicSpendKey,
    publicViewKey,
    address: moneroAddress(publicSpendKey, publicViewKey),
  };
}
